import React, { useState,useEffect } from 'react';
import { useAppStateContext } from '../../../hooks/useAppState';
import JitterBoxPlot from '../../plots/JitterBoxPlot';

// This is the component which has the hydrology box plots
export default function HydrologyFlowVolumePanel() {
  const { state, bmpState, getBmpTypeDisplayName } = useAppStateContext();
  const { analysistype } = bmpState;

  // Set the plotting data to the state hydrology indexData plot_data (rather than index Summary)
  const [boxPlotData, setBoxPlotData] = useState([]);
  
  useEffect(() => {
    // Update plot data when indexData.plot_data changes

    /* 
        The plot data is fetched and stored in global state from a "GoButton" component which is in 
          the HydrologyControls component (selectionControls folder)
    */
    let tmpPlotData = state.hydrology.indexData?.plot_data || [];

    // data points have that is_comparison attribute which allows a user to toggle whether or not they display
    // There is a comparison mode toggle button for the WQ, Hydrology and MPI tabs
    // Comparison in this case means comparing a BMP to other BMPs of the same type
    tmpPlotData = state.hydrology.comparisonMode ? tmpPlotData : tmpPlotData.filter(d => d.is_comparison !== 'true');

    // "tmp" is what will become the boxplot data (at the bottom of this useEffect hook)
    let tmp = {};
        
    // loop over the unique BMPs and create an array of objects for each BMP
    // the unique BMPs should really just be the selected BMP and the comparison data
    // also, we want the human friendly BMP Type to display, hence why we call getBmpTypeDisplayName
    // getBmpTypeDisplayName also just returns the very arg it was passed, if the value isnt found in the BMP type lookup list
    // so for individual analysis or treatment trains, it will just return d.bmp, which is what we would want

    // first get the unique bmp values (a individual site/bmp or a BMPtype, and the comparison data (Other ___ BMPs))
    // then create an object with those as the values, along with its corresponding plot data

    // as a result, the human readable BMP Type should display on the x axis tick mark
    Array.from(new Set( tmpPlotData.map((d) => getBmpTypeDisplayName(d.bmp) ) ))
        .sort((a, b) => {
            // Sort the array so that the comparator data goes last
            const aIsOther = /^other/i.test(a);
            const bIsOther = /^other/i.test(b);
            if (aIsOther && !bIsOther) return 1;
            if (!aIsOther && bIsOther) return -1;
            return 0;
        })
        .forEach((bmp) => {
            // BMP Types are stored as two digit codes in the database
            // getBmpTypeDisplayName is a method for translating the code to the actual name that a user can understand
            // In a perfect world, the anlaysis table would have the full name
            //   but in my opinion its not worth the refactoring effort
            tmp[bmp] = tmpPlotData.filter((d) => getBmpTypeDisplayName(d.bmp) === bmp) ;
        });


    setBoxPlotData(tmp);

  }, [state.hydrology?.indexData?.plot_data, state.hydrology.comparisonMode, analysistype]);
  
  
  if (!boxPlotData || Object.entries(boxPlotData).length === 0) {
    return (
      <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-4">Flow and Volume Reduction Analysis</h3>
          <p className="text-gray-600 mb-8">
            No hydrology data available. Please run a hydrology analysis in the Performance Index tab to view flow and volume reduction analysis.
          </p>
        </div>
      </div>
    );
  }

  console.log("Hydrology Box Plot Data", boxPlotData)

  return (
    <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-4">Flow and Volume Reduction Analysis</h3>
        <p className="text-gray-600">
          Box plots showing flow and volume reduction performance.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Hydrology Performance</h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Flow Reduction */}
          <div className="bg-gray-50 p-3 md:p-4 rounded">
            <h5 className="text-sm md:text-md font-medium mb-3">Peak Flow Reduction</h5>
            <JitterBoxPlot 
              data={ boxPlotData }
              accessors={['flow_reduction_efficiency']}
              yLabel="% Reduction"
              height={350}
              referenceLine={0}
              colors={['#0299ad','#329da8']}
              />
          </div>

          {/* Volume Reduction */}
          <div className="bg-gray-50 p-3 md:p-4 rounded">
            <h5 className="text-sm md:text-md font-medium mb-3">% Volume Reduction</h5>
            <JitterBoxPlot 
              data={ boxPlotData }
              accessors={['volume_reduction_efficiency']}
              yLabel="% Reduction"
              height={350}
              referenceLine={0}
              colors={['#0299ad','#329da8']}
            />
          </div>

          {/* Flow Data */}
          <div className="bg-gray-50 p-3 md:p-4 rounded">
            <h5 className="text-sm md:text-md font-medium mb-3">Influent/Effluent Peak Flow Rate</h5>
            <JitterBoxPlot 
              data={boxPlotData}
              accessors={['inflow_pfr', 'outflow_pfr']}
              legendLabels={['Influent Peak Flow Rate', 'Effluent Peak Flow Rate']}
              height={350}
              margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
              yLabel={`Peak Flow Rate (${(Object.values(boxPlotData).find(v => v.length)?.[0].pfr_units) || ""})`}
              colors={['#3182ce', '#e53e3e']}
            />
          </div>

          {/* Volume Data */}
          <div className="bg-gray-50 p-3 md:p-4 rounded">
            <h5 className="text-sm md:text-md font-medium mb-3">Volume Data</h5>
            <JitterBoxPlot 
              data={boxPlotData}
              accessors={['inflow_volume', 'outflow_volume']}
              legendLabels={['Influent Volume', 'Effluent Volume']}
              height={350}
              margin={{ top: 20, right: 20, bottom: 80, left: 60 }}
              yLabel={`Volume (${(Object.values(boxPlotData).find(v => v.length)?.[0].volume_units) || ""})`}
              colors={['#3182ce', '#e53e3e']}
            />
          </div>
        </div>
      </div>
    </div>
  );
} 